import type { ReactNode } from 'react';
export type GridValue = string | number | boolean | null;
export type GridRow = { id: string | number };
export type GridOption = { value: string; label: string };
export type GridLookup = {
  unavailable?: boolean;
  search?: (
    search: string,
    offset: number
  ) => Promise<{ options: GridOption[]; hasMore: boolean }>;
};
export type GridFilter = {
  lookup?: GridLookup;
  id: string;
  label: string;
  filterType?:
    | 'text'
    | 'integer'
    | 'ids'
    | 'date-range'
    | 'boolean'
    | 'nullable-integer';
  options?: { value: string; label: string }[];
};
export type GridColumn<T> = GridFilter & {
  id: string;
  label: string;
  getValue: (row: T) => GridValue;
  format?: (value: GridValue) => string;
  editable?: boolean;
  isOriginalValue?: (value: GridValue) => boolean;
  renderEditor?: (props: {
    row: T;
    value: string;
    onSave: (value: string) => void;
    onCancel: () => void;
  }) => ReactNode;
  type?: 'text' | 'number' | 'select' | 'boolean' | 'date';
  options?: { value: string; label: string }[];
  validate?: (value: GridValue) => string | undefined;
  sortable?: boolean;
  filterable?: boolean;
  hidden?: boolean;
  extraFilters?: GridFilter[];
};
export type GridView = {
  order: string[];
  hidden: string[];
  filters: Record<string, string>;
  filterLabels?: Record<string, Record<string, string>>;
  sort: { column: string; direction: 'asc' | 'desc' };
  pageSize: number;
};
export type GridDraft<T> = {
  original: T;
  changes: Record<string, GridValue>;
  error?: string;
};
export type GridDrafts<T> = Record<string, GridDraft<T>>;
export type SavedGridView = { id: string; name: string; view: GridView };
export type GridViewStore = {
  version: 1;
  views: SavedGridView[];
  defaultId?: string;
};

export function gridFilters<T>(columns: GridColumn<T>[]): GridFilter[] {
  return columns.flatMap((column) => [
    ...(column.filterable ? [column] : []),
    ...(column.extraFilters || []),
  ]);
}

export function defaultView<T>(columns: GridColumn<T>[]): GridView {
  return {
    order: columns.map((c) => c.id),
    hidden: columns.filter((c) => c.hidden).map((c) => c.id),
    filters: {},
    sort: { column: 'id', direction: 'asc' },
    pageSize: 25,
  };
}

export function normalizeView<T>(
  input: Partial<GridView>,
  columns: GridColumn<T>[]
): GridView {
  const fallback = defaultView(columns);
  const ids = new Set(columns.map((c) => c.id));
  const order = Array.isArray(input?.order)
    ? input.order.filter((id) => ids.has(id))
    : [];
  const hidden = Array.isArray(input?.hidden)
    ? [
        ...input.hidden.filter((id) => ids.has(id)),
        ...columns
          .filter((c) => c.hidden && !order.includes(c.id))
          .map((c) => c.id),
      ]
    : fallback.hidden;
  return {
    order: Array.from(new Set([...order, ...fallback.order])),
    hidden: hidden.length < columns.length ? hidden : fallback.hidden,
    filters: Object.fromEntries(
      Object.entries(input?.filters || {}).filter(
        ([id, value]) =>
          gridFilters(columns).some((c) => c.id === id) &&
          typeof value === 'string'
      )
    ),
    ...(input?.filterLabels
      ? {
          filterLabels: Object.fromEntries(
            Object.entries(input.filterLabels)
              .filter(
                ([id, values]) =>
                  gridFilters(columns).some((f) => f.id === id) &&
                  values &&
                  typeof values === 'object'
              )
              .map(([id, values]) => [
                id,
                Object.fromEntries(
                  Object.entries(values).filter(
                    ([, label]) => typeof label === 'string'
                  )
                ),
              ])
          ),
        }
      : {}),
    sort:
      columns.some((c) => c.id === input?.sort?.column && c.sortable) &&
      ['asc', 'desc'].includes(input?.sort?.direction)
        ? input.sort
        : fallback.sort,
    pageSize: [25, 50, 100].includes(input?.pageSize) ? input.pageSize : 25,
  };
}

export function readViews<T>(
  key: string,
  columns: GridColumn<T>[]
): GridViewStore {
  try {
    const stored = JSON.parse(localStorage.getItem(key) || 'null');
    if (stored?.version !== 1 || !Array.isArray(stored.views)) {
      return { version: 1, views: [] };
    }
    const views = stored.views
      .filter(
        (v) =>
          typeof v?.id === 'string' && typeof v?.name === 'string' && v.view
      )
      .map((v) => ({
        id: v.id,
        name: v.name,
        view: normalizeView(v.view, columns),
      }));
    return {
      version: 1,
      views,
      defaultId: views.some((v) => v.id === stored.defaultId)
        ? stored.defaultId
        : undefined,
    };
  } catch {
    return { version: 1, views: [] };
  }
}

export function changeCell<T extends GridRow>(
  drafts: GridDrafts<T>,
  row: T,
  column: GridColumn<T>,
  value: GridValue
): GridDrafts<T> {
  if (!column.editable) {
    return drafts;
  }
  const key = String(row.id);
  const original = drafts[key]?.original || row;
  const changes = { ...drafts[key]?.changes };
  // Empty inputs and absent optional values represent the same display value.
  if (
    column.isOriginalValue?.(value) ||
    String(value ?? '') === String(column.getValue(original) ?? '')
  ) {
    delete changes[column.id];
  } else {
    changes[column.id] = value;
  }
  const next = { ...drafts };
  if (Object.keys(changes).length) {
    next[key] = { original, changes };
  } else {
    delete next[key];
  }
  return next;
}

export function cellValue<T extends GridRow>(
  row: T,
  column: GridColumn<T>,
  drafts: GridDrafts<T>
): GridValue {
  const changes = drafts[String(row.id)]?.changes;
  return changes && Object.prototype.hasOwnProperty.call(changes, column.id)
    ? changes[column.id]
    : column.getValue(row);
}

export function parseValue<T>(text: string, column: GridColumn<T>): GridValue {
  if (column.renderEditor) {
    throw new Error(`${column.label}: используйте редактор ячейки`);
  }
  if (column.type === 'boolean') {
    if (['true', 'Да'].includes(text)) {
      return true;
    }
    if (['false', 'Нет'].includes(text)) {
      return false;
    }
    throw new Error(`${column.label}: выберите Да или Нет`);
  }
  if (column.type === 'date') {
    return text || null;
  }
  if (column.type === 'number') {
    if (!text.trim()) {
      return null;
    }
    const number = Number(text.replace(',', '.'));
    if (!Number.isFinite(number)) {
      throw new Error(`${column.label}: введите число`);
    }
    return number;
  }
  if (column.type === 'select') {
    if (!text) {
      return null;
    }
    const options =
      column.options?.filter((o) => o.value === text || o.label === text) || [];
    if (options.length !== 1) {
      throw new Error(`${column.label}: выберите значение из справочника`);
    }
    return options[0].value;
  }
  return text;
}

export function displayValue<T>(
  value: GridValue,
  column: GridColumn<T>
): string {
  if (column.format) {
    return column.format(value);
  }
  return (
    column.options?.find((o) => o.value === String(value))?.label ??
    String(value ?? '')
  );
}
